/**
 * Generic hook factory for config-driven reports
 *
 * Encapsulates the common 5-phase data pipeline used by all config-driven reports:
 * 1. Fetch data from API using config-defined data sources
 * 2. Normalize and extract fields from API responses
 * 3. Generate chart data via config-defined aggregations
 * 4. Calculate stat cards from primary data
 * 5. Build table data
 *
 * Both Assets by Site and Purchase Report 2 use this factory.
 */

import { useCallback, useState, useMemo } from "react";
import type { ConfigDrivenReportConfig, ReportData, ExportCSVReportResults, ExportTableChartConfig, ChartConfig } from "./types";
import {
  useReportData,
  buildFilterParams,
  useExportDetailCsv,
  extractAndNormalizeReportData,
  aggregateForChart,
} from "./utils";
import { useGetAggregateAssets } from "@hooks/use-get/aggregate-assets";
import { useReportFilterStateContext, useFilterOptionsContext } from "@hooks/use-report-filter";
import { useScreenAlert } from "@hooks/use-screen-alert";
import {
  calculateStatValue,
  validateStatCardConfig,
  periodToDateRange,
  applyStatCardFilters,
} from "./shared/stat-builder";
import { mapDbTableToPostType } from "./shared/data-source-config";

/**
 * Generic hook factory for config-driven reports following the 5-phase pipeline
 *
 * @param config Configuration for the report including data sources, charts, stats, and factory options
 * @param customStatCards Optional custom stat cards to include (from user config)
 * @returns Report data with loading, error, filter, and export states
 *
 * @example
 * const result = useConfigDrivenReport(MY_REPORT_CONFIG, customStatCards);
 */
export const useConfigDrivenReport = (
  config: ConfigDrivenReportConfig,
  customStatCards?: Record<string, any>,
  customCharts?: ChartConfig[]
): ExportCSVReportResults<ReportData> => {
  // Extract export-table chart and its properties
  // Memoize the chart lookup to ensure stable reference
  const exportTableChart = useMemo(() => {
    return config.charts?.find(
      (chart) => chart.type === "export-table"
    ) as ExportTableChartConfig | undefined;
  }, [config.charts]);

  // Memoize to prevent dependency array changes
  const exportFilename = useMemo(() => exportTableChart?.exportFilename ?? "export.csv", [exportTableChart?.exportFilename]);

  const { getAggregateAssets } = useGetAggregateAssets();
  const { filterState } = useReportFilterStateContext();
  const available = useFilterOptionsContext();
  const { exportDetailCsv } = useExportDetailCsv();
  const [exportDetailLoading, setExportDetailLoading] = useState(false);

  /**
   * Fetch raw data and transform into aggregations based on config
   * Implements the 5-phase pipeline
   */
  const fetchAndTransform = useCallback(async (): Promise<ReportData> => {
    // Guard against a corrupted/incomplete admin_config (e.g. saved with an
    // empty dataSources array) — without this, config.dataSources[0] below
    // throws a raw "Cannot read properties of undefined (reading 'id')".
    if (!config.dataSources || config.dataSources.length === 0) {
      throw new Error(
        "This report's data source configuration is missing or invalid. Use \"Restructure Report\" to fix it."
      );
    }

    // Only request year-level backend buckets for a data source when EVERY
    // date-aggregating chart drawing from it wants "year" — a data source
    // shared by mixed month/year charts must stay at the finer month
    // granularity so aggregateForChart's client-side re-bucketing can still
    // produce correct month-level charts from it.
    const allCharts = [...(config.charts || []), ...(customCharts || [])];
    const dateGranularityByDataSource: Record<string, "year"> = {};
    config.dataSources.forEach((dataSource) => {
      const chartsForSource = allCharts.filter(
        (chart) => ((chart as any).dataSource || config.dataSources[0].id) === dataSource.id
      );
      const granularities = chartsForSource
        .map((chart) => (chart as any).dateAggregation?.[0])
        .filter((g): g is "month" | "year" => g === "month" || g === "year");
      if (granularities.length > 0 && granularities.every((g) => g === "year")) {
        dateGranularityByDataSource[dataSource.id] = "year";
      }
    });

    // ============================================
    // Phase 1: Fetch Data Sources
    // ============================================
    const responses = await Promise.all(
      config.dataSources.map((dataSource) => {
        // Merge the data source's always-applied static filter into dynamicFilters so it
        // is sent to the API as a query param (filters[column][]=value) rather than
        // applied client-side. This ensures the filter works even when the filter column
        // is not included in groupBy.
        const staticFilter = dataSource.filter?.column
          ? { [dataSource.filter.column]: [...dataSource.filter.values] }
          : {};

        const params: any = {
          groupBy: dataSource.groupBy,
          postType: mapDbTableToPostType(dataSource.dbTable),
          ...buildFilterParams({ ...filterState, ...staticFilter }),
        };
        if ("sum" in dataSource && dataSource.sum) {
          params.sum = dataSource.sum;
        }
        if (dateGranularityByDataSource[dataSource.id]) {
          params.dateGranularity = dateGranularityByDataSource[dataSource.id];
        }
        return getAggregateAssets(params);
      })
    );

    // Apply frontend filtering immediately after fetch (per data source, before any aggregations)
    responses.forEach((response, index) => {
      const dataSource = config.dataSources[index];
      
      if (dataSource.filter && dataSource.filter.values.length > 0) {
        response.data = (response.data || []).filter((row: any) => {
          const rowValue = row[dataSource.filter!.column];
          return dataSource.filter!.values.some(filterValue => {
            if (filterValue === "null") {
              return rowValue === null || rowValue === undefined;
            }
            return rowValue === filterValue;
          });
        });
      }
    });

    // ============================================
    // Phase 2: Normalize and Extract Data
    // ============================================
    const dataBySource: Record<string, Record<string, any>[]> = {};
    config.dataSources.forEach((dataSource, index) => {
      dataBySource[dataSource.id] = extractAndNormalizeReportData(
        responses[index].data || [],
        dataSource.groupBy
      ) as Record<string, any>[];
    });

    // Use primary data source for table and stat calculations
    const primaryData = dataBySource[config.dataSources[0].id];

    // ============================================
    // Phase 3: Generate Chart Data
    // ============================================
    const charts: Record<string, any> = {};
    if (config.charts) {
      config.charts.forEach((chart) => {
        const dataSourceId = (chart as any).dataSource || config.dataSources[0].id;
        let dataForChart = dataBySource[dataSourceId] || primaryData;
        
        // Apply chart-level filtering if configured
        // Chart filters are applied ONLY to this chart, not to other charts or stat cards
        if (chart.filter && chart.filter.values.length > 0) {
          dataForChart = dataForChart.filter((row: any) => {
            const rowValue = row[chart.filter!.column];
            // Support special "null" string to filter for actual null/undefined values
            return chart.filter!.values.some((filterValue: string) => {
              if (filterValue === "null") {
                return rowValue === null || rowValue === undefined;
              }
              // Convert both to strings for comparison to handle type mismatches
              return String(rowValue) === String(filterValue);
            });
          });
        }
        
        // Determine the field to aggregate for this chart's data source
        const chartDataSource = config.dataSources.find(ds => ds.id === dataSourceId);
        const chartAggregateField = (chartDataSource?.sum && chartDataSource.sum.length > 0)
          ? chartDataSource.sum[0]
          : "count";

        charts[chart.id] = aggregateForChart(dataForChart, chart, {
          countBy: chartAggregateField as any,
        });
      });
    }

    // Process custom user-added charts against the primary data source
    if (customCharts && customCharts.length > 0) {
      const primaryDataSource = config.dataSources[0];
      const primaryAggField = (primaryDataSource.sum && primaryDataSource.sum.length > 0)
        ? primaryDataSource.sum[0]
        : "count";

      customCharts.forEach((chart) => {
        let dataForChart = primaryData;

        // Apply chart-level filter (same logic as config-defined charts)
        if (chart.filter && chart.filter.values.length > 0) {
          dataForChart = dataForChart.filter((row: any) => {
            const rowValue = row[chart.filter!.column];
            return chart.filter!.values.some((filterValue: string) => {
              if (filterValue === "null") {
                return rowValue === null || rowValue === undefined;
              }
              // Convert both to strings for comparison to handle type mismatches
              return String(rowValue) === String(filterValue);
            });
          });
        }

        charts[chart.id] = aggregateForChart(dataForChart, chart, {
          countBy: primaryAggField as any,
        });
      });
    }

    // ============================================
    // Phase 4: Calculate Stat Cards from Primary Data
    // ============================================
    const statCards: Record<string, number> = {};

    // Merge admin stat cards with custom stat cards
    const allStatCards = [
      ...(config.statCards || []),
      ...(customStatCards ? Object.values(customStatCards) : []),
    ];

    if (allStatCards.length > 0) {
      // Process stat cards in parallel
      await Promise.all(
        allStatCards.map(async (statCard) => {
          const validationError = validateStatCardConfig(statCard);
          if (validationError) {
            statCards[statCard.id] = 0;
            return;
          }

          // If stat card has a period filter, fetch data with period as date range filter
          if (statCard.period) {
            try {
              const dateRange = periodToDateRange(statCard.period);
              const dateColumn = statCard.periodColumn || "purchase_date"; // Default to purchase_date if not specified

              // Build filters with period as date range
              const periodFilters = {
                ...filterState,
                [dateColumn]: {
                  start: dateRange.start,
                  end: dateRange.end,
                },
              };

              // Fetch data with period filter applied at API level
              const params: any = {
                groupBy: config.dataSources[0].groupBy,
                postType: mapDbTableToPostType(config.dataSources[0].dbTable),
                ...buildFilterParams(periodFilters),
              };
              if ("sum" in config.dataSources[0] && config.dataSources[0].sum) {
                params.sum = config.dataSources[0].sum;
              }

              const response = await getAggregateAssets(params);
              let statData = response.data || [];

              // Apply stat-card-level filters (singular filter + filters array, AND-combined)
              statData = applyStatCardFilters(statData, statCard);

              // Calculate stat value (period filtering already applied by API)
              statCards[statCard.id] = calculateStatValue(statData, {
                ...statCard,
                period: undefined, // Don't apply period filter again in calculateStatValue
                periodColumn: undefined,
              });
            } catch (error) {
              statCards[statCard.id] = 0;
            }
          } else {
            // No period filter - use primary data (existing logic)
            // calculateStatValue handles:
            // 1. Stat-card-level filtering (if specified)
            // 2. Aggregation based on the stat card config
            statCards[statCard.id] = calculateStatValue(primaryData, statCard);
          }
        })
      );
    }

    // ============================================
    // Phase 5: Build Table Data
    // ============================================
    const tableData = primaryData;

    return {
      charts,
      statCards,
      tableData: tableData as any,
    };
  }, [getAggregateAssets, filterState, config, customStatCards, customCharts]);

  const { data, loading, error, refetch } = useReportData(
    { charts: {}, statCards: {}, tableData: [] },
    fetchAndTransform
  );

  const { setInfo } = useScreenAlert();

  const exportDetail = useCallback(
    async (selectedRows: Record<string, any>[], exportCols?: any[]) => {
      if (!exportCols || exportCols.length === 0) {
        setInfo("Please select columns to export");
        return;
      }
      setExportDetailLoading(true);
      await exportDetailCsv({
        selectedRows,
        exportCols,
        globalFilters: filterState,
        rowFilterFields: config.dataSources[0].groupBy,
        postType: mapDbTableToPostType(config.dataSources[0]?.dbTable),
        filename: exportFilename,
      });
      setExportDetailLoading(false);
    },
    [exportDetailCsv, filterState, config, exportFilename, setInfo]
  );

  return {
    data,
    loading,
    error,
    refetch,
    filters: filterState,
    available,
    exportDetailLoading,
    exportDetail,
  };
};

/**
 * Central export file for all report-specific hooks
 * Provides a clean barrel export pattern for importing report hooks
 */

// Report configurations (consolidated)
// All reports are defined in reports.config.ts and accessible via the REPORTS object
export { REPORTS } from "./reports.config";

export {  usePrintHandler, buildGlobalQueryParams, useExportDetailCsv } from "./utils";
export type { ExportDetailOptions } from "./types";

// Shared type exports
export type {
  ReportDataResponse,
  AggregationResponse,
  AggregateDataItem,
  ChartDataPoint,
  MultiLineSeriesData,
  HeatmapDatum,
  ExportCSVReportResults,
  ConfigDrivenReportResult,
} from "./types";



